import React, { useEffect, useState, useRef } from 'react';
import { recupererContenuFormulaire, listerPiecesJointes, supprimerPieceJointe, uploadDocumentDemande } from '../Services';
import { PieceJointe } from '../models/PieceJointe';
import FormulairesDossier from './FormulairesDossier';
import ConformitePjDialog from './ConformitePjDialog';
import ListePiecesJointes from './ListePiecesJointes';
import AdresseGlissable from './AdresseGlissable';
import { Paper, Typography, Box, Chip, Button, CircularProgress, Alert } from '@mui/material';
import { CloudUpload as CloudUploadIcon, FactCheckOutlined as FactCheckIcon } from '@mui/icons-material';

const EMAIL_CONTACT = 'contact@visamonde.com';

interface InfoFormulaireProps {
  chemin: string;
  // Mail du compte client de la demande (colonne email de la liste des demandes)
  emailClient?: string | null;
}

const InfoFormulaire: React.FC<InfoFormulaireProps> = ({ chemin, emailClient }) => {
  const [contenu, setContenu] = useState<Record<string, any> | null>(null);
  const [piecesJointes, setPiecesJointes] = useState<PieceJointe[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const [uploadError, setUploadError] = useState<string>('');
  const [normesOuvert, setNormesOuvert] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (chemin) {
      recupererContenuFormulaire(chemin)
        .then((donnees) => {
          setContenu(donnees);
        })
        .catch((erreur) => console.error('Erreur lors de la récupération du formulaire:', erreur));

      chargerPiecesJointes();
    }
  }, [chemin]);

  const chargerPiecesJointes = () => {
    listerPiecesJointes(chemin)
      .then((fichiers) => {
        setPiecesJointes(fichiers);
      })
      .catch((erreur) => console.error('Erreur lors de la récupération des pièces jointes:', erreur));
  };

  // Document déposé par erreur : retiré du dossier (copie gardée dans supprimes/ sur le serveur)
  const handleSupprimer = async (piece: PieceJointe) => {
    setUploadSuccess('');
    setUploadError('');
    try {
      await supprimerPieceJointe(chemin, piece.nom);
      setUploadSuccess(`Document "${piece.nom}" supprimé.`);
      chargerPiecesJointes();
      setTimeout(() => setUploadSuccess(''), 5000);
    } catch (error) {
      console.error('Erreur lors de la suppression du document:', error);
      setUploadError(error instanceof Error ? error.message : 'Erreur lors de la suppression du document.');
      setTimeout(() => setUploadError(''), 5000);
    }
  };

  const handleButtonClick = () => {
    setUploadSuccess('');
    setUploadError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadSuccess('');
    setUploadError('');

    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await uploadDocumentDemande(chemin, formData);
      setUploadSuccess(`Document "${response.fileName}" ajouté avec succès!`);

      // Recharger les pièces jointes pour afficher le nouveau document
      setTimeout(() => {
        chargerPiecesJointes();
      }, 500);

      // Effacer le message de succès après 5 secondes
      setTimeout(() => {
        setUploadSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Erreur lors de l\'upload du document:', error);
      setUploadError('Erreur lors de l\'ajout du document. Veuillez réessayer.');

      // Effacer le message d'erreur après 5 secondes
      setTimeout(() => {
        setUploadError('');
      }, 5000);
    } finally {
      setUploading(false);
      // Réinitialiser l'input pour permettre l'upload du même fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Fonction pour sélectionner le texte de la valeur lors du survol
  const handleMouseOver = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    const target = event.target as HTMLSpanElement;
    const range = document.createRange();
    const selection = window.getSelection();
    range.selectNodeContents(target);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  // Filtrer les clés que vous ne souhaitez pas afficher
  const filteredContenu = contenu
    ? Object.entries(contenu).filter(
        ([cle]) => !['etatFormulaire', 'idUtilisateur', 'idVoyageur'].includes(cle)
      )
    : [];

  if (!contenu) return <p>Chargement des informations du formulaire...</p>;

  return (
    <Paper elevation={3} sx={{ padding: 2 }}>
      <Box sx={{ mb: 2, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <strong>Informations du formulaire</strong>
        <Chip label={`Dossier: ${chemin}`} size="small" color="primary" variant="outlined" />
      </Box>

      {/* Adresses attrapables à la souris pour être déposées ailleurs : contact Visamonde et client de la demande */}
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <AdresseGlissable email={EMAIL_CONTACT} />
        {emailClient && <AdresseGlissable email={emailClient} client />}
      </Box>

      {/* Section pièces jointes : toujours affichée pour permettre l'ajout même quand le dossier n'en a aucune */}
      <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            Pièces jointes:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Contrôle des pièces du dossier face à la norme de son visa (menu Pièces jointes) + versions conformes */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FactCheckIcon />}
              onClick={() => setNormesOuvert(true)}
              sx={{ textTransform: 'none' }}
            >
              Normes PJ
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
              onClick={handleButtonClick}
              disabled={uploading}
              sx={{ textTransform: 'none' }}
            >
              {uploading ? 'Upload en cours...' : 'Ajouter un document'}
            </Button>
          </Box>
        </Box>

        {/* Messages de succès et d'erreur */}
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            {uploadSuccess}
          </Alert>
        )}
        {uploadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {uploadError}
          </Alert>
        )}

        {/* Liste des pièces jointes (corbeille pour retirer un document déposé par erreur) ou message si aucune */}
        <ListePiecesJointes fichiers={piecesJointes} onSupprimer={handleSupprimer} />

        {/* Input file caché */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar"
        />
      </Box>

      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Détails du formulaire:
        </Typography>
        {filteredContenu.length > 0 ? (
          filteredContenu.map(([cle, valeur], index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                cursor: 'text',
                userSelect: 'none',
                mb: 0.5,
                p: 0.5,
                '&:hover': {
                  bgcolor: '#f0f0f0',
                  borderRadius: 1
                }
              }}
            >
              <Typography component="span" sx={{ fontWeight: 500, minWidth: { xs: 'auto', sm: '200px' } }}>
                {`${cle}: `}
              </Typography>
              {/* Appliquer onMouseOver uniquement sur la valeur */}
              <Typography
                component="span"
                onMouseOver={handleMouseOver}
                sx={{
                  userSelect: 'text',
                  flex: 1,
                  textAlign: { xs: 'left', sm: 'right' }
                }}
              >
                {valeur && typeof valeur === 'object'
                  ? Object.entries(valeur).map(([k, v]) => `${k}: ${v || ''}`).join(' | ')
                  : (valeur || '-')}
              </Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            Aucune information disponible pour ce formulaire
          </Typography>
        )}
      </Box>
      <FormulairesDossier chemin={chemin} />
      <ConformitePjDialog open={normesOuvert} chemin={chemin} onClose={() => setNormesOuvert(false)} />
    </Paper>
  );
};

export default InfoFormulaire;
